import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem' };
const inputStyle = { background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '0.5rem 0.75rem', color: colors.textPrimary, fontFamily, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { background: colors.goldActiveBg, color: colors.textPrimary, border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '0.5rem 1.25rem', cursor: 'pointer', fontFamily, fontWeight: 700, fontSize: '0.9rem' };
const btnDanger = { background: 'rgba(220,53,69,0.8)', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 0.9rem', cursor: 'pointer', fontFamily, fontWeight: 600, fontSize: '0.85rem' };

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', name: '', role: 'customer_service' });

  useEffect(() => { loadStaff(); }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const data = await api.getAccounts('customer_service');
      setStaff(data.users || []);
    } catch (err) {
      setError('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      await api.createAccount({ ...form, role: 'customer_service' });
      setSuccess(`Customer service account "${form.name}" created successfully.`);
      setForm({ username: '', password: '', name: '', role: 'customer_service' });
      setShowForm(false);
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (staffUser) => {
    try {
      await api.updateAccount(staffUser.id, { is_active: !staffUser.is_active });
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ color: colors.textPrimary, margin: 0, fontSize: '1.1rem', fontWeight: 700, fontFamily }}>Staff Management</h2>
        <button style={btnPrimary} onClick={() => { setShowForm(v => !v); setError(null); setSuccess(null); }}>
          {showForm ? 'Cancel' : '+ New CS Account'}
        </button>
      </div>

      {error && <div style={{ background: 'rgba(220,53,69,0.15)', border: '1px solid rgba(220,53,69,0.4)', borderRadius: 6, padding: '0.75rem 1rem', color: colors.negative, marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</div>}
      {success && <div style={{ background: 'rgba(40,167,69,0.15)', border: '1px solid rgba(40,167,69,0.4)', borderRadius: 6, padding: '0.75rem 1rem', color: colors.odds, marginBottom: '1rem', fontSize: '0.9rem' }}>{success}</div>}

      {showForm && (
        <div style={{ ...card, border: `1px solid ${colors.gold}` }}>
          <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '0.95rem', fontFamily }}>Create Customer Service Account</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.3rem', fontFamily }}>Full Name</label>
                <input
                  style={inputStyle}
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Support Agent"
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.3rem', fontFamily }}>Username</label>
                <input
                  style={inputStyle}
                  value={form.username}
                  onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                  placeholder="login username"
                  required
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.3rem', fontFamily }}>Password</label>
                <input
                  style={inputStyle}
                  type="password"
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Strong password required"
                  required
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.75rem', borderTop: `1px solid ${colors.goldMuted}` }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '0.5rem 1.25rem', color: colors.textMuted, cursor: 'pointer', fontFamily }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Creating...' : 'Create Account'}</button>
            </div>
          </form>
        </div>
      )}

      <div style={card}>
        <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '0.9rem', fontFamily }}>Customer Service Accounts</h3>
        {loading ? (
          <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem' }}>Loading...</p>
        ) : staff.length === 0 ? (
          <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem' }}>No customer service accounts yet. Create one above.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                  {['Name', 'Username', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ color: colors.textMuted, padding: '0.5rem', textAlign: 'left', fontWeight: 'normal', fontFamily }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${colors.sectionBg}` }}>
                    <td style={{ padding: '0.6rem 0.5rem', color: colors.textPrimary, fontFamily }}>{s.name}</td>
                    <td style={{ padding: '0.6rem 0.5rem', color: colors.textMuted, fontFamily }}>{s.username}</td>
                    <td style={{ padding: '0.6rem 0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 20, background: s.is_active ? 'rgba(40,167,69,0.2)' : 'rgba(108,117,125,0.2)', color: s.is_active ? colors.odds : '#6c757d' }}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.5rem' }}>
                      <button onClick={() => toggleActive(s)} style={{ ...btnDanger, background: s.is_active ? 'rgba(220,53,69,0.7)' : 'rgba(40,167,69,0.7)', fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
                        {s.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
